import * as React from 'react';
import PropTypes from 'prop-types';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full border px-3 py-1 text-sm font-semibold w-fit whitespace-nowrap shrink-0 [&>svg]:size-3.5 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-sky-100 text-sky-700 hover:bg-sky-200',
        secondary: 'border-transparent bg-purple-100 text-purple-700 hover:bg-purple-200',
        destructive: 'border-transparent bg-rose-100 text-rose-700 hover:bg-rose-200',
        success: 'border-transparent bg-emerald-100 text-emerald-700 hover:bg-emerald-200',
        warning: 'border-transparent bg-orange-100 text-orange-700 hover:bg-orange-200',
        neutral: 'border-transparent bg-gray-100 text-gray-700 hover:bg-gray-200',
        outline: 'text-foreground',
        ghost: 'text-foreground border-transparent hover:bg-gray-100',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const Badge = React.forwardRef(({ className, variant, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      ref={ref}
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
});

Badge.displayName = 'Badge';

Badge.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.oneOf([
    'default',
    'secondary',
    'destructive',
    'success',
    'warning',
    'neutral',
    'outline',
    'ghost',
  ]),
  asChild: PropTypes.bool,
};

export { Badge, badgeVariants }
